import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SustainwizeNavbar from "@/components/landing/SustainwizeNavbar";
import Footer from "@/components/landing/Footer";
import sustainwizeContactBg from "@/assets/sustainwize-contact-bg.png";

const SustainwizeContactUs = () => {
  const { t, language } = useLanguage();
  const isThai = language === "th";
  const [isLoading, setIsLoading] = useState(true);
  
  const benefits = [
    "Centralized sustainability data management",
    "Real-time progress tracking across all properties",
    "Document compilation for sustainability standards"
  ];

  const metadata = {
    en: {
      title: "Book a Demo | Sustainwize",
      description: "Get in touch with Sustainwize for a free demo of our hotel sustainability management platform. Book a consultation with our experts.",
      locale: "en_US"
    },
    th: {
      title: "นัดสาธิต | Sustainwize",
      description: "ติดต่อ Sustainwize เพื่อนัดสาธิตฟรี ปรึกษาผู้เชี่ยวชาญด้านความยั่งยืนของเรา",
      locale: "th_TH"
    }
  };

  useEffect(() => {
    // Load Pipedrive script dynamically
    const script = document.createElement("script");
    script.src = "https://webforms.pipedrive.com/f/loader";
    script.async = true;
    document.body.appendChild(script);

    // Show loading for 1.5s to allow form to load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{metadata[language].title}</title>
        <meta name="description" content={metadata[language].description} />
        <link rel="canonical" href="https://carbonwize.io/sustainwize/contact-us" />
        
        {/* hreflang tags for multilingual SEO */}
        <link rel="alternate" hrefLang="en" href="https://carbonwize.io/en/sustainwize/contact-us" />
        <link rel="alternate" hrefLang="th" href="https://carbonwize.io/th/sustainwize/contact-us" />
        <link rel="alternate" hrefLang="x-default" href="https://carbonwize.io/sustainwize/contact-us" />
        
        {/* Open Graph - Language Aware */}
        <meta property="og:title" content={metadata[language].title} />
        <meta property="og:description" content={metadata[language].description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://carbonwize.io/sustainwize/contact-us" />
        <meta property="og:image" content="https://carbonwize.io/sustainwize-og-thumbnail.png" />
        <meta property="og:locale" content={metadata[language].locale} />
        <meta property="og:site_name" content="Sustainwize" />
        
        {/* Structured Data - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://carbonwize.io/sustainwize"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Book a Demo",
                "item": "https://carbonwize.io/sustainwize/contact-us"
              }
            ]
          })}
        </script>
      </Helmet>

      <SustainwizeNavbar />

      <main className="min-h-screen pt-[65px]" style={{ backgroundColor: "#373530" }}>
        {/* Hero Section with Form */}
        <section 
          className="relative overflow-hidden min-h-[calc(100vh-65px)] flex items-center"
          style={{
            backgroundImage: `url(${sustainwizeContactBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="container mx-auto px-3 sm:px-4 lg:px-8 py-8 lg:py-12">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-12 items-center">
              {/* Left Content */}
              <div className="relative z-10">
                <h1 className={`font-bold text-[#373530] mb-4 ${isThai ? "text-3xl lg:text-4xl" : "text-4xl lg:text-5xl"}`}>
                  Begin your sustainability journey with Sustainwize
                </h1>

                <p className={`text-[#373530]/80 mb-5 max-w-xl ${isThai ? "text-base lg:text-lg" : "text-lg lg:text-xl"}`}>
                  Sustainwize helps hotels manage sustainability data, track progress, and achieve certifications.
                </p>

                {/* Benefits */}
                <div className="space-y-3 mb-6">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0" />
                      <span className="text-[#373530] text-lg">{benefit}</span>
                    </div>
                  ))}
                </div>

                <p className="text-[#373530] text-lg mb-8">
                  By filling out the form, our sustainability experts will contact you shortly.
                </p>
              </div>

              {/* Right - Contact Form */}
              <div className="relative z-10 flex justify-center lg:justify-start">
                <div className="bg-card rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl border border-border min-h-[400px] relative w-full sm:w-[500px]">
                  {/* Loading overlay - shown on top */}
                  {isLoading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-card rounded-2xl z-10">
                      <div className="w-8 h-8 border-4 border-[#fd6158] border-t-transparent rounded-full animate-spin" />
                      <p className="text-muted-foreground text-sm mt-4">Loading form...</p>
                    </div>
                  )}
                  {/* Pipedrive form - ALWAYS in DOM */}
                  <div 
                    className="pipedriveWebForms" 
                    data-pd-webforms="https://webforms.pipedrive.com/f/ccV5D2Odr4ibpvdpGhlEMsjxisuyQi4XozRX8W2nPb4r2hm091t2ftRV02Nmfg5kpt" 
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default SustainwizeContactUs;